import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Paper,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { callAPI } from "../../functions/api";
import { BarChart, XAxis, YAxis, Bar, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import LoadingModal from "../../components/modals/loadingModal";
import utc from "dayjs/plugin/utc";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { WidgetContainer } from "../../components/shared/widgetContainer/widgetContainer";
import { devLog, devWarn } from "../../functions/logging";
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);
const COLORS = [
  "#7E4553",
  "#70C930",
  "#F9CA00",
  "#BA1650",
  "#E97F03",
  "#9D419B",
  "#DDA0DD",
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
];

const transformEc2DataForChart = (raw) => {
  const environments = raw.environments;
  const allOsTypes = new Set();

  const chartData = environments.map((env) => {
    const row = { environment: env };
    const osList = raw.metrics[env] || [];

    osList.forEach(({ name, value }) => {
      row[name] = value;
      allOsTypes.add(name);
    });

    return row;
  });

  return {
    chartData,
    osKeys: Array.from(allOsTypes),
  };
};

const getAllFridays = () => {
  const fridays = [];
  let date = dayjs().startOf("week").day(5); // go to Friday this week
  if (date.isAfter(dayjs())) date = date.subtract(7, "day");

  while (date.isAfter(dayjs("2024-01-01"))) {
    fridays.push(date);
    date = date.subtract(7, "day");
  }

  return fridays.reverse(); // oldest → newest
};
const getLastFriday = () => {
  const today = dayjs();
  return today.day() >= 5 ? today.day(5) : today.subtract(today.day() + 2, "day").day(5);
};

const WeeklyReportsView = () => {
  const theme = useTheme();
  const previewRef = useRef(null);
  const [report, setReport] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState(dayjs(getLastFriday()));

  const [ec2ByOs, setEc2ByOs] = useState([]);
  const [currentCostData, setCurrentCostData] = useState([]);
  const [tenableEc2VulnDailyStats, setTenableEc2VulnDailyStats] = useState([]);
  const [weeklyJiraData, setWeeklyJiraData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [availableReports, setAvailableReports] = useState([]);
  const [validFridays, setValidFridays] = useState([]);
  const [reportSections, setReportSections] = useState([]);

  useEffect(() => {
    const fetchCharts = async () => {
      try {
        const ec2 = await callAPI("assets/ec2/ec2sbyos", "GET");
        const cost = await fetchCloudCurrentCostReport();
        const tenableDailyStats = await fetchVulnDataForBasicChart();
        const weeklyJira = await await callAPI(
          "ticketing/jira/team-weekly-summary",
          "GET",
          null,
          true
        );

        let allReports = await fetchData("weeklyReports");

        if (!Array.isArray(allReports)) {
          console.warn("⚠️ weeklyReports was not an array, got:", allReports);
          allReports = []; // fallback to empty array
        }

        const reportDates = allReports.map((r) => r.rowKey).filter(Boolean);
        const fridays = getAllFridays();

        setTenableEc2VulnDailyStats(tenableDailyStats);
        setEc2ByOs(ec2);
        setCurrentCostData(cost);
        setWeeklyJiraData(weeklyJira);
        setAvailableReports(reportDates);
        setValidFridays(fridays);
      } catch (err) {
        console.error("❌ Error loading charts:", err);
      }
    };

    fetchCharts();
  }, []);

  useEffect(() => {
    if (!selectedDate || availableReports.length === 0) return;

    const key = selectedDate.format("YYYYMMDD");
    devLog("🗓️ Date changed, checking report for:", key);

    if (availableReports.includes(key)) {
      loadReportByDate(key);
    } else {
      devWarn("❌ Report not found, clearing...");
      setReport("");
    }
  }, [selectedDate, availableReports]);
  const teamVelocityChartData = weeklyJiraData.map((t) => ({
    name: t.team,
    ThisWeek: t.velocity,
    LastWeekAvg:
      t.previousVelocities && t.previousVelocities.length > 1
        ? Math.round(
            t.previousVelocities.slice(1).reduce((a, b) => a + b, 0) /
              (t.previousVelocities.length - 1)
          )
        : 0,
  }));
  const handleFetchReport = async () => {
    setLoading(true);
    setModalMessage("CORA is Generating report...");
    setModalOpen(true);
    try {
      const response = await callAPI("getWeeklyReportHttp");
      if (response.ok) {
        const text = await response.text(); // because it's text/markdown
        const sections = text.split(/\n*---\n*/g).map((s) => s.trim());

        setReportSections(sections);
        setReport(sections.join("\n\n---\n\n"));
      } else {
        setReport("Failed to generate report.");
      }
    } catch (err) {
      console.error("Failed to fetch report:", err);
      setReport("Error generating report.");
    } finally {
      setLoading(false);
      setModalOpen(false);
    }
  };
  const handleGeneratePDF = async () => {
    setModalMessage("CORA is Generating PDF Report...");
    setModalOpen(true);

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 10;

    const elements = [...previewRef.current.querySelectorAll(".pdf-section")];
    let isFirstPage = true;

    // 🔥 Load CORA logo
    const logo = new Image();
    logo.src = "/cora-logo.png"; // ensure it's in your public/assets folder
    await new Promise((res) => (logo.onload = res));

    for (let i = 0; i < elements.length; i++) {
      const section = elements[i];

      const cloned = section.cloneNode(true);
      Object.assign(cloned.style, {
        position: "absolute",
        top: "0",
        left: "-9999px",
        width: "1000px",
        margin: "0",
        padding: "20px",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: "Arial, sans-serif",
        fontSize: "16px",
        lineHeight: "1.6",
      });

      const hiddenEls = cloned.querySelectorAll(".hide-in-pdf, hr");
      hiddenEls.forEach((el) => {
        el.setAttribute("data-orig-display", el.style.display);
        el.style.display = "none";
      });

      document.body.appendChild(cloned);
      await new Promise((r) => setTimeout(r, 300));

      const canvas = await html2canvas(cloned, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      document.body.removeChild(cloned);
      hiddenEls.forEach((el) => {
        el.style.display = el.getAttribute("data-orig-display") || "";
      });

      const imgData = canvas.toDataURL("image/png");
      const imgProps = pdf.getImageProperties(imgData);

      const availableWidth = pageWidth - margin * 2;
      const imgHeight = (imgProps.height * availableWidth) / imgProps.width;

      if (!isFirstPage) {
        pdf.addPage();
      }

      if (isFirstPage) {
        const logoWidth = 50;
        const logoHeight = (logo.height * logoWidth) / logo.width;
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 10;

        // Add logo at the top
        pdf.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

        // Shift content down below logo
        const contentY = logoY + logoHeight + 5;
        pdf.addImage(imgData, "PNG", margin, contentY, availableWidth, imgHeight);
      } else {
        pdf.addImage(imgData, "PNG", margin, 10, availableWidth, imgHeight);
      }

      isFirstPage = false;
    }

    const friday = dayjs().day() >= 5 ? dayjs().day(5) : dayjs().add(5 - dayjs().day(), "day");
    const fileName = `Cloud Operations Weekly Report - Week Ending (${friday.format(
      "dddd D MMMM YYYY"
    )}).pdf`;

    pdf.save(fileName);
    setModalOpen(false);
  };

  const handleSaveReport = async () => {
    setModalMessage("Saving weekly report...");
    setModalOpen(true);

    try {
      const selected = dayjs(selectedDate);
      const friday =
        selected.day() >= 5 ? selected.day(5) : selected.add(5 - selected.day(), "day");

      const fridayUtc = friday.utc(); // ensure UTC

      const rowKey = friday.utc().format("YYYYMMDD"); // ✅ No dashes; // safe for RowKey
      const weekEndingIso = fridayUtc.toISOString(); // readable timestamp if needed

      const reportRecord = {
        rowKey: rowKey,
        timestamp: new Date().toISOString(), // use current time in UTC
        weekEnding: weekEndingIso,
        markdown: report,
        summaryData: JSON.stringify(weeklyJiraData),
        ec2ByOs: JSON.stringify(ec2ByOs),
        currentCostData: JSON.stringify(currentCostData),
        tenableStats: JSON.stringify(tenableEc2VulnDailyStats),
      };

      await submitData("weeklyReports", [reportRecord], rowKey);
    } catch (error) {
      console.error("❌ Failed to save report:", error);
    } finally {
      setModalOpen(false);
    }
  };

  const CustomDateTick = ({ x, y, payload }) => {
    const date = dayjs(payload.value);
    const dayName = date.format("ddd");
    const dayNum = date.format("D");
    const suffix = dayNum === "1" ? "st" : dayNum === "2" ? "nd" : dayNum === "3" ? "rd" : "th";
    const top = `${dayName} ${dayNum}${suffix}`;
    const month = date.format("MMM");
    const year = date.format("YYYY");

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={5} dy={10} textAnchor="middle" fill="#ccc" fontSize={16}>
          {top}
        </text>
        <text
          x={0}
          y={0}
          dy={30} // more vertical space from the first line
          textAnchor="middle"
          fill="#aaa"
          fontSize={14}
        >
          {month}
        </text>
        <text
          x={0}
          y={0}
          dy={45} // more vertical space for the year line
          textAnchor="middle"
          fill="#888"
          fontSize={12}
        >
          {year}
        </text>
      </g>
    );
  };
  const loadReportByDate = async (rowKey) => {
    try {
      setModalMessage("Loading report...");
      setModalOpen(true);
      const allReports = await fetchData("weeklyReports", true);
      const match = allReports.find((r) => r.rowKey === rowKey);

      if (match?.markdown) {
        const sections = match.markdown.split(/\n*---\n*/g).map((s) => s.trim());

        setReport(sections.join("\n\n---\n\n")); // ✅ Set report FIRST
        setReportSections(sections); // ✅ Then set sections
      } else {
        setReport("⚠️ No content found for that report.");
        setReportSections([]);
      }
    } catch (e) {
      console.error("Error loading report:", e);
      setReport("❌ No report found.");
      setReportSections([]);
    } finally {
      setModalOpen(false);
    }
  };

  useEffect(() => {
    const sections = report.split(/\n*---\n*/g).map((s) => s.trim());
    setReportSections(sections);
  }, [report]);

  const selectedKey = selectedDate.format("YYYYMMDD");
  const reportExists = availableReports.includes(selectedKey);

  return (
    <Box
      sx={{
        height: "calc(100vh - 70px)",
        p: 2,
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          backgroundColor: "#414141",
          boxShadow: 3,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <DatePicker
            label="Select Week"
            value={selectedDate}
            onChange={(date) => {
              if (!date) return;
              const friday = dayjs(date).day(5);
              setSelectedDate(friday);
            }}
            shouldDisableDate={(date) => {
              const d = dayjs(date);
              const isFriday = d.day() === 5;
              const isInPastOrToday = d.isSameOrBefore(dayjs(), "day");
              return !(isFriday && isInPastOrToday);
            }}
            renderDay={(day, _value, DayComponentProps) => {
              const dateKey = day.format("YYYYMMDD");
              const isReportDate = availableReports.includes(dateKey);
              const isSelected = selectedDate?.isSame(day, "day");

              return (
                <Box
                  sx={{
                    position: "relative",
                    width: "36px",
                    height: "36px",
                    m: "2px",
                    borderRadius: "50%",
                    backgroundColor: isSelected ? "#70C930" : undefined,
                  }}
                >
                  <Typography
                    {...DayComponentProps}
                    sx={{
                      lineHeight: "36px",
                      textAlign: "center",
                      fontWeight: isReportDate ? "bold" : "normal",
                      color: isReportDate ? "#fff" : undefined,
                    }}
                  >
                    {day.date()}
                  </Typography>
                  {isReportDate && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 4,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: "#F9CA00",
                      }}
                    />
                  )}
                </Box>
              );
            }}
            slotProps={{
              textField: {
                sx: {
                  height: 40,
                  "& .MuiInputBase-root": {
                    height: 40,
                    minHeight: 40,
                  },
                  "& .MuiInputBase-input": {
                    height: "1.5em",
                    padding: "8px 14px",
                  },
                },
              },
            }}
          />

          <Button
            variant="contained"
            onClick={handleFetchReport}
            disabled={loading || (reportExists && reportSections.length > 0)}
            sx={{ height: "40px" }}
          >
            {loading
              ? "Generating..."
              : reportExists && reportSections.length > 0
                ? "Report Exists"
                : "Generate Report"}
          </Button>

          <Button variant="outlined" onClick={handleSaveReport} sx={{ height: "40px" }}>
            Save Report
          </Button>
          <Button variant="outlined" onClick={handleGeneratePDF} sx={{ height: "40px" }}>
            Export as PDF
          </Button>
        </Box>
      </Box>

      {/* Main Layout */}
      <Grid
        container
        spacing={2}
        sx={{ flexGrow: 1, height: "calc(100% - 70px)", paddingTop: "30px" }}
      >
        {/* Left - Editor */}
        <Grid item xs={6} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <WidgetContainer loaded={true} title="CORA Report Editor">
            <Paper
              ref={previewRef}
              sx={{
                flexGrow: 1,
                overflow: "auto",
                p: 1,
                marginTop: "10px",

                color: theme.palette.text.primary,
                fontFamily: "monospace",
              }}
            >
              {reportSections?.length > 1 && (
                <Box
                  sx={{
                    flexGrow: 1,
                    overflow: "auto",
                    //backgroundColor: theme.palette.background.paper,
                    borderRadius: 1,
                  }}
                >
                  <TextField
                    multiline
                    fullWidth
                    variant="standard"
                    value={report}
                    onChange={(e) => setReport(e.target.value)}
                    sx={{
                      px: 2,
                      pt: 2,
                      width: "100%",
                      height: "100%",
                      "& .MuiInputBase-root": {
                        height: "100%",
                        alignItems: "flex-start",
                        fontFamily: "monospace",
                        color: theme.palette.text.primary,
                      },
                    }}
                    InputProps={{
                      style: { height: "100%" },
                    }}
                  />
                </Box>
              )}
            </Paper>
          </WidgetContainer>
        </Grid>

        {/* Right - Preview */}
        <Grid item xs={6} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <WidgetContainer loaded={true} title="CORA Report Preview">
            <Paper
              ref={previewRef}
              sx={{
                flexGrow: 1,
                overflow: "auto",
                p: 3,
                marginTop: "10px",

                color: theme.palette.text.primary,
                fontFamily: "monospace",
              }}
            >
              {reportSections?.length > 1 && (
                <Box sx={{ mb: 4 }} className="pdf-section">
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ marginBottom: "15px", fontWeight: "bold" }}
                  >
                    Weekly Report for CloudOps Week Ending (
                    {dayjs(selectedDate).format("dddd D MMMM YYYY")})
                  </Typography>
                  <Typography variant="h6" gutterBottom sx={{ marginBottom: "15px" }}>
                    Executive Summary
                  </Typography>
                  {reportSections[0] && (
                    <ReactMarkdown
                      children={reportSections[0]}
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: (props) => <Typography variant="h5" {...props} />,
                        h2: (props) => <Typography variant="h6" {...props} />,
                        p: (props) => <Typography paragraph {...props} />,
                        ul: (props) => (
                          <ul
                            style={{
                              listStylePosition: "outside", // ensures bullet is outside the text
                              paddingLeft: "24px", // creates space between bullet and text
                              marginBottom: "16px",
                            }}
                            {...props}
                          />
                        ),
                        li: ({ children }) => (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              marginBottom: "8px",
                              fontSize: "14px",
                              lineHeight: "1.6",
                            }}
                          >
                            <span
                              style={{
                                minWidth: "16px",
                                marginRight: "8px",
                                fontWeight: "bold",
                              }}
                            >
                              •
                            </span>
                            <span>{children}</span>
                          </div>
                        ),
                        table: (props) => (
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              marginTop: "1rem",
                              marginBottom: "1rem",
                            }}
                            {...props}
                          />
                        ),
                        th: (props) => (
                          <th
                            style={{
                              border: "1px solid #ccc",
                              padding: 6,
                              backgroundColor: "#f6f6f6",
                              color: "#333",
                            }}
                            {...props}
                          />
                        ),
                        td: (props) => (
                          <td style={{ border: "1px solid #ccc", padding: 6 }} {...props} />
                        ),
                      }}
                    />
                  )}
                </Box>
              )}
              {reportSections?.length > 1 && (
                <Box className="pdf-section" sx={{ mt: 4 }}>
                  <Box sx={{ mb: 4 }}>
                    <hr className="hide-in-pdf" />
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ marginBottom: "15px" }}>
                    Team Achievements
                  </Typography>
                  <ReactMarkdown
                    children={reportSections[1]}
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: (props) => <Typography variant="h5" {...props} />,
                      h2: (props) => <Typography variant="h6" {...props} />,
                      h3: (props) => <Typography variant="h7" {...props} />,
                      p: (props) => <Typography paragraph {...props} />,
                      ul: (props) => (
                        <ul
                          style={{
                            listStylePosition: "outside", // ensures bullet is outside the text
                            paddingLeft: "24px", // creates space between bullet and text
                            marginBottom: "16px",
                          }}
                          {...props}
                        />
                      ),
                      li: ({ children }) => (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            marginBottom: "8px",
                            fontSize: "14px",
                            lineHeight: "1.6",
                          }}
                        >
                          <span
                            style={{
                              minWidth: "16px",
                              marginRight: "8px",
                              fontWeight: "bold",
                            }}
                          >
                            •
                          </span>
                          <span>{children}</span>
                        </div>
                      ),
                      table: (props) => (
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            marginTop: "1rem",
                            marginBottom: "1rem",
                          }}
                          {...props}
                        />
                      ),
                      th: (props) => (
                        <th
                          style={{
                            border: "1px solid #ccc",
                            padding: 6,
                            backgroundColor: "#f6f6f6",
                            color: "#333",
                          }}
                          {...props}
                        />
                      ),
                      td: (props) => (
                        <td style={{ border: "1px solid #ccc", padding: 6 }} {...props} />
                      ),
                    }}
                  />

                  <Grid container spacing={3} sx={{ mb: 4, mt: 0 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom sx={{ marginBottom: "15px" }}>
                        Weekly Team Summary
                      </Typography>
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Team</TableCell>
                              <TableCell>New </TableCell>
                              <TableCell>Closed</TableCell>
                              <TableCell>Velocity</TableCell>
                              <TableCell>Avg Cycle Time</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {weeklyJiraData.map((row) => (
                              <TableRow key={row.team}>
                                <TableCell>{row.team}</TableCell>
                                <TableCell>{row.newTickets}</TableCell>
                                <TableCell>{row.closedPerWeek?.[0] ?? row.closedTickets}</TableCell>
                                <TableCell>
                                  {row.velocity}
                                  {row.velocityTrend === "up" && (
                                    <span
                                      style={{
                                        color: "#70C930",
                                        marginLeft: 8,
                                      }}
                                    >
                                      ▲ +{row.velocityDelta}
                                    </span>
                                  )}
                                  {row.velocityTrend === "down" && (
                                    <span
                                      style={{
                                        color: "#BA1650",
                                        marginLeft: 8,
                                      }}
                                    >
                                      ▼ {row.velocityDelta}
                                    </span>
                                  )}
                                  {row.velocityTrend === "same" && (
                                    <span style={{ color: "#aaa", marginLeft: 8 }}>–</span>
                                  )}
                                </TableCell>
                                <TableCell>{row.averageCycleTime}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                        Velocity is the number of tickets closed this week. Arrows indicate whether
                        it's higher or lower compared to the average of the last 3 weeks.
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Grid>
                        <Typography variant="h6" gutterBottom sx={{ marginBottom: "15px" }}>
                          Team Velocity
                        </Typography>
                      </Grid>
                      <Grid sx={{ height: "400px" }}>
                        <Box
                          sx={{
                            backgroundColor: "#121212",
                            p: 2,
                            borderRadius: 2,
                            height: "100%",
                          }}
                        >
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={teamVelocityChartData}
                              margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#444" />{" "}
                              <XAxis
                                dataKey="name"
                                stroke="#ccc"
                                margin={{
                                  top: 5,
                                  right: 0,
                                  left: 0,
                                  bottom: "0",
                                }}
                              />
                              <YAxis stroke="#ccc" />
                              <Legend verticalAlign="bottom" height={36} />
                              <Bar dataKey="ThisWeek" fill="#70C930" name="This Week" />
                              <Bar dataKey="LastWeekAvg" fill="#F9CA00" name="Prev Avg" />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {reportSections?.length > 1 && (
                <Box className="pdf-section" sx={{ mt: 4 }}>
                  <hr className="hide-in-pdf" />

                  <Typography variant="h6" gutterBottom>
                    KPIs & Metrics
                  </Typography>
                  {/* Charts section */}

                  <Box sx={{ mb: 4 }}>
                    {/* align center content */}
                    <Box sx={{ textAlign: "center", mb: 0 }}>
                      <Typography
                        sx={{
                          textAlign: "center",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                        }}
                        gutterBottom
                      >
                        Vulnerabilities by Severity (Last 5 Working Days)
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#121212",
                        p: 2,
                        borderRadius: 2,

                        mb: 2,
                        mt: 0,
                      }}
                    >
                      {" "}
                      {tenableEc2VulnDailyStats.length > 0 && (
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart
                            data={tenableEc2VulnDailyStats}
                            margin={{ top: 0, right: 0, left: 0, bottom: 30 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#444" />{" "}
                            {/* 👈 adds grid lines */}
                            <XAxis
                              dataKey="date"
                              stroke={theme.palette.text.primary}
                              tick={<CustomDateTick />}
                            />
                            <YAxis stroke={theme.palette.text.primary} />
                            <Legend
                              verticalAlign="bottom"
                              wrapperStyle={{
                                position: "absolute",
                                bottom: "0px",
                              }}
                            />
                            {/* or position bottom */}
                            <Bar dataKey="Low" stackId="a" fill="#70C930" />
                            <Bar dataKey="Medium" stackId="a" fill="#F9CA00" />
                            <Bar dataKey="High" stackId="a" fill="#E97F03" />
                            <Bar dataKey="Critical" stackId="a" fill="#BA1650" />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ marginBottom: "15px", marginTop: "15px" }}
                    >
                      Weekly Vunlnerability Summary
                    </Typography>
                    <ReactMarkdown
                      children={reportSections[2]}
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: (props) => <Typography variant="h5" {...props} />,
                        h2: (props) => <Typography variant="h6" {...props} />,
                        p: (props) => <Typography paragraph {...props} />,
                        li: ({ children }) => (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              marginBottom: "8px",
                              fontSize: "14px",
                              lineHeight: "1.6",
                            }}
                          >
                            <span
                              style={{
                                minWidth: "16px",
                                marginRight: "8px",
                                fontWeight: "bold",
                              }}
                            >
                              •
                            </span>
                            <span>{children}</span>
                          </div>
                        ),
                        table: (props) => (
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              marginTop: "1rem",
                              marginBottom: "1rem",
                            }}
                            {...props}
                          />
                        ),
                        th: (props) => (
                          <th
                            style={{
                              border: "1px solid #ccc",
                              padding: 6,
                              backgroundColor: "#f6f6f6",
                              color: "#333",
                            }}
                            {...props}
                          />
                        ),
                        td: (props) => (
                          <td style={{ border: "1px solid #ccc", padding: 6 }} {...props} />
                        ),
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ textAlign: "center", mb: 0 }}>
                      <Typography
                        sx={{
                          textAlign: "center",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                        }}
                        gutterBottom
                      >
                        EC2 Breakdown by OS and Environment
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#121212",
                        p: 2,
                        borderRadius: 2,
                        marginTop: "10px",
                      }}
                    >
                      {ec2ByOs?.metrics && (
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={transformEc2DataForChart(ec2ByOs).chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#444" />{" "}
                            <XAxis dataKey="environment" stroke={theme.palette.text.primary} />
                            <YAxis stroke={theme.palette.text.primary} />
                            <Legend />
                            {transformEc2DataForChart(ec2ByOs).osKeys.map((os, i) => (
                              <Bar
                                key={os}
                                dataKey={os}
                                stackId="a"
                                fill={COLORS[i % COLORS.length]}
                              />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ marginBottom: "15px", marginTop: "15px" }}
                    >
                      Weekly EC2 Observations
                    </Typography>
                    <ReactMarkdown
                      children={reportSections[3]}
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: (props) => <Typography variant="h5" {...props} />,
                        h2: (props) => <Typography variant="h6" {...props} />,
                        p: (props) => <Typography paragraph {...props} />,
                        li: ({ children }) => (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              marginBottom: "8px",
                              fontSize: "14px",
                              lineHeight: "1.6",
                            }}
                          >
                            <span
                              style={{
                                minWidth: "16px",
                                marginRight: "8px",
                                fontWeight: "bold",
                              }}
                            >
                              •
                            </span>
                            <span>{children}</span>
                          </div>
                        ),
                        table: (props) => (
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              marginTop: "1rem",
                              marginBottom: "1rem",
                            }}
                            {...props}
                          />
                        ),
                        th: (props) => (
                          <th
                            style={{
                              border: "1px solid #ccc",
                              padding: 6,
                              backgroundColor: "#f6f6f6",
                              color: "#333",
                            }}
                            {...props}
                          />
                        ),
                        td: (props) => (
                          <td style={{ border: "1px solid #ccc", padding: 6 }} {...props} />
                        ),
                      }}
                    />
                  </Box>
                </Box>
              )}

              {reportSections?.length > 1 && (
                <Box className="pdf-section" sx={{ mt: 4 }}>
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ textAlign: "center", mb: 0 }}>
                      <Typography
                        sx={{
                          textAlign: "center",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                        }}
                        gutterBottom
                      >
                        Current AWS Spend This Month
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#121212",
                        p: 2,
                        borderRadius: 2,
                        marginTop: "10px",
                      }}
                    >
                      {currentCostData?.chartData?.length > 0 && (
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart
                            data={currentCostData.chartData}
                            margin={{
                              top: 20,
                              right: 10,
                              left: 20,
                              bottom: 10,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#444" />{" "}
                            <XAxis dataKey="environment" stroke={theme.palette.text.primary} />
                            <YAxis
                              stroke={theme.palette.text.primary}
                              tickFormatter={(value) =>
                                `$${value.toLocaleString(undefined, {
                                  minimumFractionDigits: 0,
                                })}`
                              }
                            />
                            <Legend />
                            {currentCostData.serviceKeys
                              .filter((svc) => svc !== "Total")
                              .map((svc, i) => (
                                <Bar
                                  key={svc}
                                  dataKey={svc}
                                  stackId="a"
                                  fill={COLORS[i % COLORS.length]}
                                />
                              ))}
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ marginBottom: "15px", marginTop: "15px" }}
                    >
                      Current Monthly Cost Analysis
                    </Typography>
                    <ReactMarkdown
                      children={reportSections[4]}
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: (props) => <Typography variant="h5" {...props} />,
                        h2: (props) => <Typography variant="h6" {...props} />,
                        p: (props) => <Typography paragraph {...props} />,
                        li: ({ children }) => (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              marginBottom: "8px",
                              fontSize: "14px",
                              lineHeight: "1.6",
                            }}
                          >
                            <span
                              style={{
                                minWidth: "16px",
                                marginRight: "8px",
                                fontWeight: "bold",
                              }}
                            >
                              •
                            </span>
                            <span>{children}</span>
                          </div>
                        ),
                        table: (props) => (
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              marginTop: "1rem",
                              marginBottom: "1rem",
                            }}
                            {...props}
                          />
                        ),
                        th: (props) => (
                          <th
                            style={{
                              border: "1px solid #ccc",
                              padding: 6,
                              backgroundColor: "#f6f6f6",
                              color: "#333",
                            }}
                            {...props}
                          />
                        ),
                        td: (props) => (
                          <td style={{ border: "1px solid #ccc", padding: 6 }} {...props} />
                        ),
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Paper>
          </WidgetContainer>
        </Grid>
      </Grid>
      <LoadingModal open={modalOpen} message={modalMessage} />
    </Box>
  );
};

export default WeeklyReportsView;
